import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, errorLine } from '../../api/client';
import type { AdminOrg } from '../../api/types';
import { formatDay, plural } from '../../lib/format';
import { orgPath } from '../org';
import { NewOrgModal } from './new-org-modal';

/**
 * The org list. `null` is "still loading" and renders nothing — the head above
 * already says so — and an empty list is an installation with no tenants yet.
 *
 * Every row is a link now rather than a selection: the name is one, and Open is
 * the same address as a button. There is no selected row any more, because there
 * is no detail below the table to select *for* — opening an org leaves this page.
 *
 * Exported for the reason `MembersTable` is: the copy sweep renders both states
 * from fixtures, and the words that outlive a rename are the ones in an empty
 * state and a column header.
 */
export function OrgsTable({ orgs }: { orgs: AdminOrg[] | null }) {
  if (orgs === null) return null;
  if (orgs.length === 0) return <div className="empty-line">No organizations yet.</div>;
  return (
    <table className="table">
      <thead>
        <tr>
          <th>Organization</th>
          <th>Vertical</th>
          <th>Created</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {orgs.map((org) => (
          <tr key={org.id}>
            <td>
              <Link className="link" to={orgPath(org.id)}>
                {org.name}
              </Link>
              <div className="sub">{org.slug}</div>
            </td>
            <td className="quiet-cell">{org.vertical}</td>
            <td className="quiet-cell">{formatDay(org.created_at)}</td>
            <td className="actions">
              <Link className="btn act tiny" to={orgPath(org.id)}>
                Open
              </Link>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * **The organizations page** — the table, and the one button that opens the
 * form.
 *
 * It used to be this table *plus* the create form *plus* whichever org was
 * selected, which is how a page with four endpoints behind it grew past a
 * screen. Both of the other two have moved: the form into {@link NewOrgModal},
 * the org detail onto `routes/org`. What is left is a list, which is what the
 * page is called.
 *
 * There is no `settled` fold-back here any more either. It existed because a
 * spend limit was written *below* this table and the row above it went stale;
 * the write now happens on a page of its own, which reads its own copy of the
 * list and folds its own writes back into it.
 */
export function Orgs() {
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState<AdminOrg[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    let live = true;
    api
      .listOrgs()
      .then((answer) => {
        if (!live) return;
        setOrgs(answer.orgs);
        setError(null);
      })
      .catch((caught: unknown) => {
        if (live) setError(errorLine(caught));
      });
    return () => {
      live = false;
    };
  }, []);

  return (
    <>
      <div className="head">
        <div className="titles">
          <div className="title">Organizations</div>
          <div className="subline">
            {orgs === null ? 'Loading.' : plural(orgs.length, 'organization', 'organizations')} on
            this server.
          </div>
        </div>
        <div className="actions-right">
          <Link className="btn act" to="/demos">
            Demo organizations
          </Link>
          <button type="button" className="btn act" onClick={() => setCreating(true)}>
            New organization
          </button>
        </div>
      </div>

      {error ? (
        <div className="flag row">
          <div className="label">Error</div>
          <div className="detail">{error}</div>
        </div>
      ) : null}

      <OrgsTable orgs={orgs} />

      {/* Straight to the new org's members tab, which is the next thing the
          operator does: a tenant with nobody in it is not yet a tenant. The list
          behind is not re-read, because this page is being left. */}
      {creating ? (
        <NewOrgModal
          onClose={() => setCreating(false)}
          onCreated={(org) => {
            setCreating(false);
            navigate(orgPath(org.id));
          }}
        />
      ) : null}
    </>
  );
}
